<script lang="ts">
  interface Summary {
    id: number
    name: string
    aliases: string[]
    internal_only: boolean
    mentions_count: number
  }

  interface SuggestionPair {
    a_id: number
    b_id: number
  }

  interface Payload {
    suggestions: SuggestionPair[]
  }

  type Kind = 'person' | 'topic'

  let kind = $state<Kind>('person')
  let apiRoot = $derived(`/api/admin/${kind}s`)
  let rootKey = $derived(`${kind}s`)
  let titlePrimary = $derived(kind === 'topic' ? 'TOPICS' : 'PERSONS')
  const titleSecondary = 'DUPLICATE REVIEW'

  let entities = $state<Summary[]>([])
  let suggestions = $state<SuggestionPair[]>([])
  let loading = $state(true)
  let error = $state('')
  let browseQuery = $state('')
  let duplicateQuery = $state('')
  let selected = $state<Summary | null>(null)

  const sortedEntities = $derived(
    [...entities].sort((a, b) => a.name.localeCompare(b.name)),
  )

  const filteredBrowse = $derived.by(() => {
    const q = browseQuery.trim().toLowerCase()
    if (!q) return sortedEntities
    return sortedEntities.filter(e => matchesTerm(e, q))
  })

  const duplicateCandidates = $derived.by(() => {
    if (!selected) return []
    const q = duplicateQuery.trim().toLowerCase()
    const base = sortedEntities.filter(e => e.id !== selected.id)
    if (!q) return base
    return base.filter(e => matchesTerm(e, q))
  })

  $effect(() => {
    kind
    selected = null
    fetchIndex()
  })

  async function fetchIndex() {
    loading = true
    error = ''
    try {
      const response = await fetch(apiRoot, { credentials: 'same-origin' })
      if (!response.ok) {
        error = `Failed to load (${response.status})`
        return
      }
      const payload = (await response.json()) as Payload & Record<string, Summary[]>
      entities = payload[rootKey] ?? []
      suggestions = payload.suggestions ?? []
    } catch (e) {
      error = 'Network error'
    } finally {
      loading = false
    }
  }

  function matchesTerm(entity: Summary, term: string): boolean {
    if (entity.name.toLowerCase().includes(term)) return true
    return (entity.aliases || []).some(a => a.toLowerCase().includes(term))
  }

  function pickEntity(item: Summary) {
    selected = item
    duplicateQuery = ''
  }

  function clearSelected() {
    selected = null
    duplicateQuery = ''
  }

  function startMerge(candidate: Summary) {
    if (!selected) return
    const [winnerId, loserId] = candidate.mentions_count > selected.mentions_count
      ? [candidate.id, selected.id]
      : [selected.id, candidate.id]
    window.location.href = `/admin/merge?type=${kind}&winner=${winnerId}&loser=${loserId}`
  }

  function entityById(id: number): Summary | undefined {
    return entities.find(e => e.id === id)
  }

  function mergeHref(pair: SuggestionPair): string {
    const [winner, loser] = pickWinnerLoser(pair)
    return `/admin/merge?type=${kind}&winner=${winner}&loser=${loser}`
  }

  function pickWinnerLoser(pair: SuggestionPair): [number, number] {
    const a = entityById(pair.a_id)
    const b = entityById(pair.b_id)
    if (!a || !b) return [pair.a_id, pair.b_id]
    if ((b.mentions_count ?? 0) > (a.mentions_count ?? 0)) return [b.id, a.id]
    return [a.id, b.id]
  }

  function nameOf(id: number): string {
    return entityById(id)?.name ?? `#${id}`
  }
</script>

<div class="console">
  <header class="console-header">
    <div class="header-label">
      <div class="kind-toggle">
        <button class="kind-tab" class:active={kind === 'person'} onclick={() => (kind = 'person')}>PERSONS</button>
        <button class="kind-tab" class:active={kind === 'topic'} onclick={() => (kind = 'topic')}>TOPICS</button>
      </div>
      <div class="label-secondary">{titleSecondary}</div>
    </div>
    <a class="back-btn" href="/admin">BACK</a>
  </header>

  {#if selected}
    <div class="sticky-bar">
      <div class="selected-card">
        <div class="selected-label">SELECTED · LOOKING FOR DUPLICATE OF</div>
        <div class="selected-name-row">
          <span class="selected-name">{selected.name}</span>
          <span class="selected-meta">{selected.mentions_count} mentions · {selected.aliases.length} aliases</span>
          <button type="button" class="ghost-btn" onclick={clearSelected}>CANCEL</button>
        </div>
      </div>
      <input
        type="text"
        class="filter-input"
        bind:value={duplicateQuery}
        placeholder="Filter candidates by name or alias…"
        aria-label="Filter duplicate candidates"
      />
    </div>
  {:else}
    <div class="sticky-bar">
      <input
        type="text"
        class="filter-input"
        bind:value={browseQuery}
        placeholder="Filter {titlePrimary.toLowerCase()} by name or alias…"
        aria-label="Filter {kind}s"
      />
    </div>
  {/if}

  {#if loading}
    <div class="status pulse">LOADING {titlePrimary}…</div>
  {:else if error}
    <div class="status error">{error.toUpperCase()}</div>
  {:else if selected}
    <div class="result-count">
      {duplicateCandidates.length} CANDIDATE{duplicateCandidates.length === 1 ? '' : 'S'} · PICK ONE TO COMPARE
    </div>
    <ul class="entity-list">
      {#each duplicateCandidates as item (item.id)}
        <li class="entity-row">
          <button type="button" class="entity-button" onclick={() => startMerge(item)}>
            <span class="entity-name">
              {item.name}
              {#if item.aliases.length > 0}
                <span class="alias-hint">({item.aliases.join(', ')})</span>
              {/if}
            </span>
            <span class="entity-meta">{item.mentions_count} mentions</span>
          </button>
        </li>
      {/each}
    </ul>
  {:else}
    <div class="result-count">
      {filteredBrowse.length} / {entities.length} · CLICK ONE TO FIND ITS DUPLICATE
    </div>
    <ul class="entity-list">
      {#each filteredBrowse as item (item.id)}
        <li class="entity-row">
          <button type="button" class="entity-button" onclick={() => pickEntity(item)}>
            <span class="entity-name">
              {item.name}
              {#if item.aliases.length > 0}
                <span class="alias-hint">({item.aliases.join(', ')})</span>
              {/if}
            </span>
            <span class="entity-meta">{item.mentions_count} mentions</span>
          </button>
        </li>
      {/each}
    </ul>

    {#if !loading && !error && suggestions.length > 0}
      <details class="suggestions-fold">
        <summary>AUTO-SUGGESTIONS · {suggestions.length} PAIR{suggestions.length === 1 ? '' : 'S'}</summary>
        <ul class="pair-list">
          {#each suggestions as pair (`${pair.a_id}-${pair.b_id}`)}
            <li class="pair-row">
              <a class="pair-link" href={mergeHref(pair)}>
                <span class="pair-name">{nameOf(pair.a_id)}</span>
                <span class="pair-vs">↔</span>
                <span class="pair-name">{nameOf(pair.b_id)}</span>
                <span class="pair-cta">REVIEW</span>
              </a>
            </li>
          {/each}
        </ul>
      </details>
    {/if}
  {/if}
</div>

<style>
  .console {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    background: var(--color-surface-0);
    font-family: var(--font-mono);
  }

  .console-header,
  .sticky-bar,
  .status,
  .result-count,
  .entity-list,
  .suggestions-fold {
    padding-left: 2rem;
    padding-right: 2rem;
  }

  .console-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 1rem;
    padding-top: 2rem;
    padding-bottom: 1.5rem;
    border-bottom: 1px solid var(--color-border-default);
  }

  .header-label {
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
  }

  .label-primary {
    font-size: 1.5rem;
    font-weight: 700;
    letter-spacing: 0.1em;
    color: var(--color-text-primary);
  }

  .kind-toggle {
    display: flex;
    gap: 0.25rem;
  }

  .kind-tab {
    font-size: 1.5rem;
    font-weight: 700;
    letter-spacing: 0.1em;
    color: var(--color-text-muted);
    background: none;
    border: none;
    padding: 0 0.25rem;
    cursor: pointer;
    transition: color 0.1s;
  }

  .kind-tab:hover {
    color: var(--color-text-secondary);
  }

  .kind-tab.active {
    color: var(--color-text-primary);
    border-bottom: 2px solid var(--color-accent-primary);
  }

  .label-secondary {
    font-size: 0.75rem;
    font-weight: 500;
    letter-spacing: 0.15em;
    color: var(--color-text-muted);
  }

  .back-btn {
    padding: 0.625rem 1.25rem;
    background: var(--color-surface-3);
    border: 1px solid var(--color-border-strong);
    color: var(--color-text-primary);
    font-size: 0.75rem;
    font-weight: 600;
    letter-spacing: 0.1em;
    text-decoration: none;
    transition: all 0.1s;
  }

  .back-btn:hover {
    background: var(--color-surface-4);
    border-color: var(--color-accent-primary);
  }

  .status {
    padding: 2rem;
    font-size: 0.875rem;
    letter-spacing: 0.1em;
    color: var(--color-text-secondary);
  }

  .status.error {
    color: var(--color-accent-playhead);
  }

  .status.pulse {
    animation: pulse 1.5s ease-in-out infinite;
  }

  @keyframes pulse {
    0%, 100% { opacity: 0.4; }
    50% { opacity: 1; }
  }

  .section-header {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    margin-bottom: 0.75rem;
    padding-bottom: 0.5rem;
    border-bottom: 1px dashed var(--color-border-subtle);
    font-size: 0.6875rem;
    font-weight: 600;
    letter-spacing: 0.15em;
    color: var(--color-text-muted);
  }

  .count {
    color: var(--color-text-secondary);
  }

  .sticky-bar {
    position: sticky;
    top: 0;
    z-index: 10;
    background: var(--color-surface-0);
    padding-top: 0.75rem;
    padding-bottom: 0.75rem;
    margin-bottom: 0.75rem;
    border-bottom: 1px solid var(--color-border-default);
  }

  .filter-input {
    width: 100%;
    box-sizing: border-box;
    padding: 0.625rem 0.875rem;
    background: var(--color-surface-2);
    border: 1px solid var(--color-border-default);
    color: var(--color-text-primary);
    font-family: var(--font-mono);
    font-size: 0.875rem;
  }

  .filter-input:focus {
    outline: none;
    border-color: var(--color-accent-primary);
  }

  .selected-card {
    margin-bottom: 0.5rem;
    padding: 0.75rem 0.875rem;
    background: color-mix(in srgb, var(--color-accent-primary) 10%, transparent);
    border: 1px solid var(--color-accent-primary);
  }

  .selected-label {
    font-size: 0.625rem;
    font-weight: 700;
    letter-spacing: 0.15em;
    color: var(--color-accent-primary);
    margin-bottom: 0.375rem;
  }

  .selected-name-row {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  .selected-name {
    font-size: 1rem;
    font-weight: 600;
    color: var(--color-text-primary);
  }

  .selected-meta {
    font-size: 0.6875rem;
    color: var(--color-text-muted);
  }

  .ghost-btn {
    margin-left: auto;
    padding: 0.375rem 0.75rem;
    background: transparent;
    border: 1px solid var(--color-border-strong);
    color: var(--color-text-primary);
    font-family: var(--font-mono);
    font-size: 0.625rem;
    font-weight: 700;
    letter-spacing: 0.15em;
    cursor: pointer;
  }

  .ghost-btn:hover {
    border-color: var(--color-accent-primary);
    color: var(--color-accent-primary);
  }

  .suggestions-fold {
    margin-top: 2rem;
    padding-top: 1rem;
    padding-bottom: 2rem;
    border-top: 1px dashed var(--color-border-subtle);
  }

  .suggestions-fold summary {
    font-size: 0.6875rem;
    font-weight: 600;
    letter-spacing: 0.15em;
    color: var(--color-text-muted);
    cursor: pointer;
    margin-bottom: 0.75rem;
  }

  .alias-hint {
    margin-left: 0.5rem;
    font-size: 0.6875rem;
    color: var(--color-text-muted);
  }

  .pair-list,
  .entity-list {
    list-style: none;
    padding-top: 0;
    padding-bottom: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  .pair-row,
  .entity-row {
    padding: 0.625rem 0.875rem;
    background: var(--color-surface-2);
    border: 1px solid var(--color-border-default);
  }

  .pair-link {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    text-decoration: none;
    color: var(--color-text-primary);
  }

  .pair-link:hover {
    color: var(--color-accent-primary);
  }

  .pair-name {
    font-size: 0.8125rem;
  }

  .pair-vs {
    color: var(--color-text-muted);
    font-size: 0.75rem;
  }

  .pair-cta {
    margin-left: auto;
    font-size: 0.625rem;
    font-weight: 700;
    letter-spacing: 0.15em;
    color: var(--color-accent-primary);
  }

  .result-count {
    font-size: 0.625rem;
    letter-spacing: 0.15em;
    color: var(--color-text-muted);
    margin-bottom: 0.5rem;
  }

  .entity-row {
    padding: 0;
  }

  .entity-button {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    padding: 0.625rem 0.875rem;
    background: transparent;
    border: none;
    color: inherit;
    font-family: inherit;
    font-size: inherit;
    text-align: left;
    cursor: pointer;
  }

  .entity-button:hover {
    background: var(--color-surface-3);
  }

  .entity-name {
    font-size: 0.8125rem;
    color: var(--color-text-primary);
  }

  .entity-meta {
    font-size: 0.6875rem;
    color: var(--color-text-muted);
  }

</style>
